<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import mockLiveData from '@/assets/mockLiveData.json';

import { useI18n } from 'vue-i18n';
const { t } = useI18n();

// Interfaces pour typer les données
interface LiveDataItem {
  Label: string;
  Value: number | boolean;
}

interface LiveDataPlugin {
  Zone: string;
  Data: LiveDataItem[];
}

interface LiveDataResponse {
  livedata: LiveDataPlugin[];
}

const dataStore = useDataStore();
const liveData = ref<LiveDataResponse | null>(null);
const error = ref<string | null>(null);

// Récupération des données en direct
const fetchLiveData = async () => {
  try {
    // Chargement direct des données mockées depuis le fichier JSON
    liveData.value = mockLiveData as LiveDataResponse;
    error.value = null; // Réinitialiser l'erreur en cas de succès
  } catch (err) {
    error.value = `${t('fetcherror')}`;
    console.error('Erreur fetchLiveData:', err);
  }
};

// Récupérer les données pour un PBR spécifique
const getDataForPBR = (pbrNumber: string): LiveDataItem[] => {
  if (!pbrNumber || !liveData.value?.livedata) {
    console.warn('getDataForPBR: Paramètres manquants ou livedata non défini');
    return [];
  }

  const Zone = getZoneForPBR(pbrNumber);
  const plugin = liveData.value.livedata.find(p => p.Zone === Zone);

  if (!plugin) {
    console.warn(`getDataForPBR: Plugin ${Zone} non trouvé pour ${pbrNumber}`);
    return [];
  }

  const pbrNum = pbrNumber.replace(/[^0-9]/g, '');
  const filteredData = plugin.Data.filter(item =>
    item.Label.includes(`B${pbrNum.padStart(2, '0')}`)
  );

  // Définir les Label attendus selon le numéro de PBR
  const num = parseInt(pbrNum, 10);
  const wantedLabels = num <= 20
    ? [
      `AI_B${pbrNum.padStart(2, '0')}_Temp`,
      `AI_B${pbrNum.padStart(2, '0')}_pH`,
    ]
    : [
      `AI_B${pbrNum.padStart(2, '0')}_Temp`,
      `AI_B${pbrNum.padStart(2, '0')}_pH`,
    ];

  return filteredData.filter(item => wantedLabels.includes(item.Label));
};

// Mapper les numéros de PBR aux noms de plugins
const getZoneForPBR = (pbrNumber: string): string => {
  const num = parseInt(pbrNumber.replace(/[^0-9]/g, ''), 10);
  if (isNaN(num)) {
    console.warn(`getZoneForPBR: Numéro invalide pour ${pbrNumber}`);
    return '';
  }
  if (num >= 1 && num <= 10) return 'PBR01to10';
  if (num >= 11 && num <= 20) return 'PBR11to20';
  if (num >= 21 && num <= 33) return 'PBR21to33';
  if (num >= 34 && num <= 43) return 'PBR34to43';
  console.warn(`getZoneForPBR: Aucun plugin pour PBR${num}`);
  return '';
};

// Interface pour les données groupées
interface GroupedData {
  label: string;
  values: {
    value: number;
    displayValue: string | number;
    unit?: string;
    suffix?: string;
  }[];
}

// Grouper les données pour l'affichage
const groupDataForDisplay = (items: LiveDataItem[]): GroupedData[] => {
  const groups: Record<string, GroupedData> = {};

  items.forEach(item => {
    let groupKey = '';
    if (item.Label.includes('Temp')) groupKey = 'temperature';
    else if (item.Label.includes('pH')) groupKey = 'ph';
    else return; // Ignorer les autres types

    if (!groups[groupKey]) {
      groups[groupKey] = { label: groupKey, values: [] };
    }

    const suffix = item.Label.match(/_(A|B)$/)?.[1] || '';
    const unit = item.Label.includes('Temp') ? 'C°' : '';

    const numericValue = typeof item.Value === 'number' ? item.Value : 0;
    let displayValue: string | number = numericValue;

    groups[groupKey].values.push({
      value: numericValue,
      displayValue,
      suffix: suffix || undefined,
      unit: unit || undefined,
    });
  });

  return Object.values(groups);
};

// Initialiser et surveiller les changements
onMounted(fetchLiveData);
watch(() => dataStore.selectedCulture, fetchLiveData, { deep: true });
</script>

<template>
  <div v-if="dataStore.selectedCulture?.pbr?.numero_pbr?.startsWith('B')" class="module">
    <div v-if="error" class="error">{{ error }}</div>
    <div v-else-if="liveData && dataStore.selectedCulture" class="live-data-wrapper">
      <h2>{{ $t('liveData') }} {{ dataStore.selectedCulture.pbr.numero_pbr }}</h2>
      <div class="live-data-container">
        <div v-if="getDataForPBR(dataStore.selectedCulture.pbr.numero_pbr).length === 0" class="no-data">
          {{ $t('noLiveData') }} {{ dataStore.selectedCulture.pbr.numero_pbr }}
        </div>
        <div
          v-for="group in groupDataForDisplay(getDataForPBR(dataStore.selectedCulture.pbr.numero_pbr))"
          class="data-group" :key="group.label">
          <strong>{{ $t(group.label) }}</strong>
          <div v-for="(val, index) in group.values" class="data-value" :key="index">
            <template v-if="group.label">
              {{ val.suffix ? `${val.suffix}: ` : '' }}
              {{ typeof val.displayValue === 'number' ? val.displayValue.toFixed(2) : val.displayValue }} {{ val.unit }}
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.module {
  padding: 1rem;
}

.live-data-wrapper {
  border-radius: 8px;
}

.live-data-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.data-group {
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.data-value {
  margin-top: 0.5rem;
}

.no-data,
.error {
  padding: 1rem;
  color: #d32f2f;
  font-weight: 500;
}
</style>